
import { useMemo } from 'react'

import { Button } from '@mui/material'
import { GridCellParams } from '@mui/x-data-grid'
import { format, parseJSON } from 'date-fns'
import Decimal from 'decimal.js'

import { IGridColumn } from 'components/common'
import { DATE_TIME_FORMAT, PackageTypes } from 'constants/index'

interface IGridColumnExtended extends IGridColumn {
  valueOptions?: any[]
  getActions?: (params: GridCellParams<any>) => React.ReactNode
}

const useGetAvailableLoadsColumns = (handleSubmitBidClick) => {
  const columns: IGridColumnExtended[] = useMemo(
    () => [
      {
        field: 'actions',
        headerName: 'Bid',
        width: 100,
        type: 'actions',
        renderCell: ({ id, row }) => {
          const hasBid = row?.hasDriverPlacedBid
          const isBidActive = row?.isDriverBidActive
          const disabled = !isBidActive && hasBid

          return (
            <Button
              variant={hasBid ? 'outlined' : 'contained'}
              onClick={handleSubmitBidClick(id)}
              disabled={disabled}>
              {hasBid ? 'Edit' : 'Bid'}
            </Button>
          )
        },
      },
      { field: 'ourReference', headerName: 'Booking Ref', width: 150, sortPath: ['id'] },
      {
        field: 'tariffCategoryName',
        headerName: 'Vehicle',
        width: 200,
        sortable: true,
      },
      {
        field: 'currentCollectionAddress',
        headerName: 'Collection Address',
        width: 200,
        sortable: true,
        sortPath: ['collectionAddressCity'],
        renderCell: ({ row }) => {
          return row?.collectionAddressCity && row?.collectionAddressOutcode
            ? `${row?.collectionAddressCity}, ${row.collectionAddressOutcode}`
            : 'N/A'
        },
      },
      {
        field: 'collectioReadyAt',
        headerName: 'Collection Ready At',
        minWidth: 200,
        sortable: true,
        valueGetter: ({ row }) => {
          return row?.collectioReadyAt
            ? format(parseJSON(row.collectioReadyAt), DATE_TIME_FORMAT)
            : 'N/A'
        },
      },
      {
        field: 'collectBy',
        headerName: 'Collect By',
        minWidth: 200,
        sortable: true,
        valueGetter: ({ row }) => {
          return row?.collectBy ? format(parseJSON(row.collectBy), DATE_TIME_FORMAT) : 'N/A'
        },
      },
      {
        field: 'lastDeliveryAddress',
        headerName: 'Delivery Address',
        width: 200,
        sortable: true,
        sortPath: ['deliveryAddressCity'],
        renderCell: ({ row }) => {
          return row?.deliveryAddressCity && row?.deliveryAddressOutcode
            ? `${row.deliveryAddressCity}, ${row.deliveryAddressOutcode}`
            : 'N/A'
        },
      },
      {
        field: 'deliverFrom',
        headerName: 'Deliver From',
        minWidth: 200,
        sortable: true,
        valueGetter: ({ row }) => {
          return row?.deliverFrom ? format(parseJSON(row.deliverFrom), DATE_TIME_FORMAT) : 'N/A'
        },
      },
      {
        field: 'deliverBy',
        headerName: 'Deliver By',
        minWidth: 200,
        sortable: true,
        valueGetter: ({ row }) => {
          return row?.deliverFrom ? format(parseJSON(row.deliverFrom), DATE_TIME_FORMAT) : 'N/A'
        },
      },
      {
        field: 'distance',
        headerName: 'Miles',
        width: 150,
        sortable: true,
        valueGetter: ({ row }) => {
          const distance = row?.distance
          return distance ? new Decimal(distance).toDP(2, Decimal.ROUND_FLOOR).toNumber() : 'N/A'
        },
      },
      {
        field: 'packageType',
        headerName: 'Package Type',
        width: 150,
        sortable: true,
        sortPath: ['packageType'],
        valueGetter: ({ row }) => PackageTypes[row?.packageType] || 'N/A',
      },
      {
        field: 'packageSize',
        headerName: 'Package Size (L x W x H x Wt)',
        width: 250,
        sortable: true,
        sortPath: ['packageWeight'],
        valueGetter: ({ row }) => {
          const pkg =
            row?.packageLength && row?.packageWidth && row?.packageHeight && row?.packageWeight
          return pkg
            ? `${row?.packageLength}cm x ${row?.packageWidth}cm x ${row?.packageHeight}cm x ${row?.packageWeight}kg`
            : 'N/A'
        },
      },
      {
        field: 'packageQuantity',
        headerName: 'Package Quantity',
        width: 150,
        sortable: true,
        valueGetter: ({ row }) => row?.packageQuantity || 'N/A',
      },
      {
        field: 'notes',
        headerName: 'Notes',
        width: 200,
        sortable: true,
        valueGetter: ({ row }) => row?.notes || 'N/A',
      },
    ],
    [handleSubmitBidClick],
  )
  return columns
}

export default useGetAvailableLoadsColumns
